import React, { useState, useEffect, useRef } from 'react';
import PropertyCard from './PropertyCard';
import apiService from '../services/api';

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    city: '',
    type: '',
    minPrice: '',
    maxPrice: '',
    minRooms: '',
    search: ''
  });
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    applyFilters();
    // Debounced search log
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const externalUserId = localStorage.getItem('extUserId');
      apiService.logSearch({
        query: filters.search,
        city: filters.city,
        type: filters.type,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minRooms: filters.minRooms,
        externalUserId,
      }).catch(() => {});
    }, 600);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [properties, filters]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const data = await apiService.fetchProperties();
      setProperties(data);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки объектов недвижимости');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...properties];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(property => 
        (property.title || '').toLowerCase().includes(searchLower) ||
        (property.city || '').toLowerCase().includes(searchLower) ||
        (property.district || '').toLowerCase().includes(searchLower) ||
        (property.description || '').toLowerCase().includes(searchLower)
      );
    }

    // City filter
    if (filters.city) {
      filtered = filtered.filter(property => property.city === filters.city);
    }

    // Type filter
    if (filters.type) {
      filtered = filtered.filter(property => property.type === filters.type);
    }

    // Price filters
    if (filters.minPrice) {
      filtered = filtered.filter(property => property.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(property => property.price <= parseInt(filters.maxPrice));
    }

    // Rooms filter
    if (filters.minRooms) {
      filtered = filtered.filter(property => property.rooms >= parseInt(filters.minRooms));
    }

    setFilteredProperties(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      type: '',
      minPrice: '',
      maxPrice: '',
      minRooms: '',
      search: ''
    });
  };

  const getUniqueCities = () => {
    return [...new Set(properties.map(p => p.city).filter(Boolean))].sort();
  };

  const getUniqueTypes = () => {
    return [...new Set(properties.map(p => p.type).filter(Boolean))];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchProperties}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Каталог недвижимости
        </h1>
        <p className="text-gray-600">
          Найдено {filteredProperties.length} из {properties.length} объектов
        </p>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Поиск по названию, городу или описанию..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <select
            value={filters.city}
            onChange={(e) => handleFilterChange('city', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Все города</option>
            {getUniqueCities().map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Все типы</option>
            {getUniqueTypes().map(type => (
              <option key={type} value={type}>{apiService.getPropertyTypeLabel(type)}</option>
            ))}
          </select>

          <input
            type="number"
            placeholder="Мин. цена"
            value={filters.minPrice}
            onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="number"
            placeholder="Макс. цена"
            value={filters.maxPrice}
            onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="number"
            placeholder="Мин. комнат"
            value={filters.minRooms}
            onChange={(e) => handleFilterChange('minRooms', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
          />

          <button
            onClick={clearFilters}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded transition-colors"
          >
            Очистить
          </button>
        </div>
      </div>

      {/* Properties Grid */}
      {filteredProperties.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            Объекты не найдены. Попробуйте изменить фильтры.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProperties.map(property => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyList;